import React, { useEffect, useState } from "react";
import { Lock, MessageCircle, Send, TrendingUp, TrendingDown, Megaphone, Coins, Bitcoin, Trophy, Activity } from "lucide-react";
import client, { apiErr } from "../../lib/api";
import { Spinner } from "../ui-kit";
import { toast } from "sonner";

const ago = (iso) => {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 1) return "just now";
  if (m < 60) return `${Math.floor(m)}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

export function TradeBadge({ t }) {
  if (!t) return null;
  if (t.outcome) {
    const win = t.outcome === "win";
    return <span className={`text-[11px] px-2 py-0.5 border flex items-center gap-1 ${win ? "border-bull/50 text-bull" : "border-bear/50 text-bear"}`}><Trophy size={11} />{win ? "+" : ""}{t.net_pips} pips</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 border border-gold/50 text-gold flex items-center gap-1"><Activity size={11} />Running {t.running_pips ? `${t.running_pips} pips` : ""}</span>;
}

export function SignalCard({ s, contacts, i = 0, hideLockedCta = false }) {
  const buy = s.direction === "BUY";
  const sell = s.direction === "SELL";
  const tone = buy ? "var(--bull)" : sell ? "var(--bear)" : "var(--gold)";
  const Coin = s.instrument === "BTCUSD" ? Bitcoin : Coins;
  const Dir = buy ? TrendingUp : sell ? TrendingDown : Megaphone;
  return (
    <div data-testid={`feed-card-${s.id}`} className="surface relative overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500"
      style={{ animationDelay: `${Math.min(i, 8) * 60}ms`, animationFillMode: "both", borderLeft: `3px solid ${tone}` }}>
      <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full opacity-[0.12] blur-3xl" style={{ background: tone }} />
      <div className="relative p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Coin size={16} className={s.instrument === "BTCUSD" ? "text-btc" : "text-gold"} />
          <span className="font-head font-bold text-white">{s.instrument}</span>
          {s.kind === "signal" ? (
            <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold font-mono" style={{ background: tone, color: "#000" }}><Dir size={12} />{s.direction || "SIGNAL"}</span>
          ) : <span className="text-[11px] px-2 py-0.5 border border-[color:var(--border-subtle)] text-slate-300 flex items-center gap-1"><Dir size={11} />Update</span>}
          <TradeBadge t={s.trade} />
          <span className="ml-auto text-[11px] text-slate-500">{ago(s.created_at)}</span>
        </div>
        <p className="text-sm text-slate-300 whitespace-pre-line mt-3 leading-relaxed">{s.free_text}</p>
        {s.premium_text ? (
          <div className="mt-3 border border-gold/30 px-3 py-2.5" style={{ background: "rgba(212,175,55,0.05)" }}>
            <div className="text-[10px] uppercase tracking-widest text-gold mb-1">Premium · full signal</div>
            <p className="text-sm text-white whitespace-pre-line leading-relaxed" data-testid="feed-premium-text">{s.premium_text}</p>
          </div>
        ) : s.locked && !hideLockedCta && (
          <div className="mt-3 border border-gold/30 px-3 py-3 relative" style={{ background: "rgba(212,175,55,0.05)" }} data-testid="feed-locked">
            <div className="flex items-center gap-2 text-sm text-white"><Lock size={14} className="text-gold" />Premium signal (SL ke saath) locked hai</div>
            <div className="flex flex-wrap gap-2 mt-2.5">
              {contacts?.whatsapp && <a href={contacts.whatsapp} target="_blank" rel="noreferrer" data-testid="feed-unlock-whatsapp" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#25D366] text-black hover:brightness-110 transition-[filter] duration-200"><MessageCircle size={13} />WhatsApp chat</a>}
              {contacts?.telegram && <a href={contacts.telegram} target="_blank" rel="noreferrer" data-testid="feed-unlock-telegram" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#229ED9] text-white hover:brightness-110 transition-[filter] duration-200"><Send size={13} />Telegram</a>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stats({ st }) {
  const items = [["Win rate", `${st.win_rate}%`, "text-gold"], ["Net pips", `${st.net_pips >= 0 ? "+" : ""}${st.net_pips}`, st.net_pips >= 0 ? "text-bull" : "text-bear"], ["Closed trades", st.closed, "text-white"]];
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="feed-stats">
      {items.map(([l, v, c]) => (
        <div key={l} className="surface px-3 py-3 text-center"><div className={`font-head text-xl sm:text-2xl font-bold ${c}`}>{v}</div><div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{l} · this month</div></div>
      ))}
    </div>
  );
}

export function SignalFeed({ limit = 60, compact = false }) {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  useEffect(() => {
    const load = () => client.get("/hub/feed", { params: { kind: kind || undefined, limit } }).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [kind, limit]);
  if (!data) return <Spinner label="Loading signals" />;
  return (
    <div className="space-y-4" data-testid="signal-feed">
      {!compact && <Stats st={data.stats} />}
      {!compact && (
        <div className="flex gap-1.5">
          {[["", "All"], ["signal", "Signals"], ["update", "Updates"]].map(([k, l]) => (
            <button key={l} data-testid={`feed-filter-${l.toLowerCase()}`} onClick={() => setKind(k)} className={`px-3 py-1.5 text-xs border transition-colors duration-200 ${kind === k ? "border-gold text-gold bg-gold/10" : "border-[color:var(--border-subtle)] text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      )}
      {data.items.length === 0 && <div className="surface p-8 text-center text-sm text-slate-500" data-testid="feed-empty">Abhi koi signal nahi hai. Naya signal aate hi yahan dikhega.</div>}
      <div className="grid gap-3 lg:grid-cols-2">
        {data.items.map((s, i) => <SignalCard key={s.id} s={s} i={i} contacts={data.contacts} />)}
      </div>
    </div>
  );
}
